<template>
	<div v-cloak>
		<button type="button" :class="styling" :style="{ width: buttonWidth }" ref="btnFacebookLogin" :disabled="sdkLoading" :aria-busy="sdkLoading" @click="facebookLoginButtonClick">
			<img :src="urlButtonIcon" />
			<span>Sign in with Facebook</span>
		</button>
		<div class="mt-1 mb-3 text-center" ref="divFacebookLogin" v-show="error">
			<span class="error-msg">Could not sign in with Facebook. Please <a href="/contact" rel="nofollow" target="_blank">contact support</a> if this keeps happening.</span>
		</div>
	</div>
</template>




<script>
	import { mixinAxios } from '@/js/shared/DeferredVueMixins.js';
	import { getClientDateTimeString, getUrlParameterByName, parseWidthValue } from '@/js/shared/utils/common';
	import { ensureFacebookSdk } from '@/js/shared/utils/sdkLoader';
	import facebookIcon from '../../img/login-buttons/facebook/icon-facebook.svg?inline';
	import facebookIconInverted from '../../img/login-buttons/facebook/icon-facebook-inverted.svg?inline';
	// This components depends on:
	// https://connect.facebook.net/en_US/sdk.js
	// <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet">

	export default {
		mixins: [mixinAxios],
		data: () => ({
			error : false,
			sdkLoading: true,
			sdkReady: false
		}),
		props: {
			appId       : { type: String, required: true },
			version     : { type: String, required: true },
			styling     : { type: String, default: 'styling0' },
			urlRedirect : { type: String, default: null },
			width       : { type: [String, Number], default: 300 }

		},
		mounted() {
			this.setWidth();
			this.loadFacebookLoginButton();
		},
		beforeUnmount() {
			this.facebookUnmounted = true;
		},
		methods: {
			async loadFacebookLoginButton() {
				this.sdkLoading = true;
				this.error = false;
				try {
					await ensureFacebookSdk();
					if (this.facebookUnmounted) return;
					this.initFacebookJavascriptSDK();
					this.sdkReady = true;
				} catch {
					this.error = true;
				} finally {
					this.sdkLoading = false;
				}
			},
			setWidth() {
				var width = parseWidthValue(this.width);
				this.btnFacebookLogin.style.width = width;
				this.divFacebookLogin.style.width = width;
			},
			redirect() {
				var redirectionNotNull = (this.redirectionUrl != null);

				if (redirectionNotNull) {
					window.location.href = this.redirectionUrl;
				} else {
					window.location.reload();
				}
			},
			debugToken(response) {
				// todo: use /debug_token to create complete facebook access token object, just like the google button does
				FB.api('/debug_token', { 'input_token': response.authResponse.accessToken }, (resDebugToken) => {
					if (resDebugToken && !resDebugToken.error) {
						// handle result
					} else {
						// error message
						console.log('error in debugToken()');
					}

					console.log('debug_token', response2);
				});
			},
			getLoginStatus() {
				FB.getLoginStatus(function(response) {
					if (response.status === 'connected') {
						// The user is logged in and has authenticated your
						// app, and response.authResponse supplies
						// the user's ID, a valid access token, a signed
						// request, and the time the access token
						// and signed request each expire.
						var uid = response.authResponse.userID;
						var accessToken = response.authResponse.accessToken;
					} else if (response.status === 'not_authorized') {
						// The user hasn't authorized your application.  They
						// must click the Login button, or you must call FB.login
						// in response to a user gesture, to launch a login dialog.
					} else {
						// The user isn't logged in to Facebook. You can launch a
						// login dialog with a user gesture, but the user may have
						// to log in to Facebook before authorizing your application.
					}
				}, true);
			},
			_facebookLoginAttempt(response) {
				var facebookAccessToken = {
					accessToken : response?.authResponse?.accessToken,
				};

				var signInInfo = {
					facebookAccessToken : facebookAccessToken,
					clientDateTime      : getClientDateTimeString()
				};

				return this.mixinAxios().post('/api/guest/FacebookLoginAttempt', signInInfo)
					.then((resFacebookLoginAttempt) => {
							if (resFacebookLoginAttempt.data.success) {
								this.redirect();
							} else {
								this.error = true;
							}
						})
					.catch(() => {
						this.error = true;
					});
			},
			facebookLoginAttempt(response) {
				if (response?.status == 'connected') {
					return this._facebookLoginAttempt(response);
				}
				// Closing or declining the dialog leaves no authenticated session.
				// Only an explicit provider error needs failure feedback here.
				this.error = Boolean(response?.error);
			},
			facebookLoginCallback(response) {

				this.facebookLoginAttempt(response);
			},
			facebookLogin() {
				FB.login(
					this.facebookLoginCallback,
					{
						scope: 'email,public_profile',
						return_scopes: true
					}
				);
			},
			facebookLoginButtonClick() {
				if (!this.sdkReady) {
					this.loadFacebookLoginButton();
					return;
				}
				this.error = false;
				this.facebookLogin();
			},
			initFacebookJavascriptSDK() {
				FB.init({
					appId: this.appId,
					autoLogAppEvents: true,
					xfbml: true,
					version: this.version
				});
			}
		},
		computed: {
			buttonWidth() {
				return parseWidthValue(this.width);
			},
			btnFacebookLogin() {
				return this.$refs['btnFacebookLogin'];
			},
			divFacebookLogin() {
				return this.$refs['divFacebookLogin'];
			},
			urlButtonIcon() {
				switch (this.styling)
				{
					case 'styling0' : return facebookIconInverted;
					case 'styling1' : return facebookIcon;
					default         : return facebookIcon;
				}
			},
			redirectionUrl() {
				var urlFromParam = getUrlParameterByName('redirect');

				return (urlFromParam != null)
					? urlFromParam
					: (this.urlRedirect != null)
					? this.urlRedirect
					: null;
			}
		},
		watch: {
			width(newVal, oldVal) {
				this.setWidth();
			}
		}
	};
</script>




<style scoped>
	[v-cloak] {
		display:none;
	}


	/* common */

	.error-msg {
		color: red;
	}

	/* common */


	/* styling 0 */

	.styling0 {
		font-family: 'Roboto', sans-serif;
		font-weight: normal;
		font-size: 16px;
		color: #FFFFFF;
		height: 40px;
		background: #4267B2;
		padding:0px 12px;
		display:flex;
		align-items:center;
		justify-content:center;
		border:thin solid #4267B2;
		border-radius: 4px;
		cursor:pointer;
	}

	.styling0 img {
		height:22px;
		width:22px;
	}

	.styling0 span {
		margin:3px 0px 0px 10px;
	}

	/* styling 0 */


	/* styling 1 */

	.styling1 {
		font-family: 'Roboto', sans-serif;
		font-weight: normal;
		font-size: 16px;
		color: #4C4C4C;
		height: 44px;
		background: #FFFFFF;
		padding:0px 12px;
		display:flex;
		align-items:center;
		justify-content:center;
		border:thin solid #C0C0C0;
		border-radius: 4px;
		cursor:pointer;
	}

	.styling1 img {
		height:22px;
		width:22px;
	}

	.styling1 span {
		margin:3px 0px 0px 10px;
	}

	/* styling 1 */

</style>
